import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import styled from 'styled-components';

import { getCompanyAICIStats, getCompanyAICompetitiveIntelligence } from 'apis/RecommendationsAPI';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { PAGINATION_PAGE_SIZE } from 'constants/pagination';
import useDataList from 'utils/hooks/useDataList';

import AICIAvgScoreChart from './AICIAvgScoreChart';
import AICICompanyCommunityTable from './AICICompanyCommunityTable';
import AICICompanySummary from './AICICompanySummary';
import AICICompanyTableFilters, { EMPTY_COMPANY_TABLE_FILTERS } from './AICICompanyTableFilters';
import CompanyAiCompetitiveIntelligenceCategoryPills from './CompanyAiCompetitiveIntelligenceCategoryPills';
import { AI_CI_CATEGORIES, OVERALL_VIEW_KEY } from './constants';
import useRecommendationsContext from './RecommendationsContext';
import RecommendationsNotFound from './RecommendationsNotFound';
import { CompanyTableFilters } from './types';

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 200px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 10px;
`;

const ChartSection = styled.div`
  margin-bottom: 30px;
`;

const PillsSection = styled.div`
  margin-bottom: 30px;
`;

const SummarySection = styled.div`
  margin-bottom: 20px;
`;

const FiltersSection = styled.div`
  margin-bottom: 30px;
`;

const CompanyAiCompetitiveIntelligence = () => {
  const { company: selectedCompany } = useRecommendationsContext();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<string>(OVERALL_VIEW_KEY);

  const performanceParam = searchParams.get('performance');
  const strongestCategoryParam = searchParams.get('strongestCategory');
  const weakestCategoryParam = searchParams.get('weakestCategory');

  const { items, totalItemsCount, filters, setItems, setPage, setFilters } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: PAGINATION_PAGE_SIZE.SMALL,
        search: '',
        performance: performanceParam ? [performanceParam] : [],
        strongestCategories: strongestCategoryParam ? [strongestCategoryParam] : [],
        weakestCategories: weakestCategoryParam ? [weakestCategoryParam] : [],
      },
    },
  });

  const isOverall = activeTab === OVERALL_VIEW_KEY;
  const activeCategoryLabel = AI_CI_CATEGORIES.find((category) => category.key === activeTab)?.label;

  const tableFilters: CompanyTableFilters = {
    performance: filters.performance,
    strongestCategories: filters.strongestCategories,
    weakestCategories: filters.weakestCategories,
  };

  const handleFiltersChange = (next: CompanyTableFilters) => setFilters(next);
  const handleSearchChange = (value: string) => setFilters({ search: value });

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setFilters({ ...EMPTY_COMPANY_TABLE_FILTERS, search: '' });
  };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['ai-competitive-intelligence-company-stats', selectedCompany?.id],
    queryFn: () => getCompanyAICIStats(selectedCompany?.id ?? -1),
    enabled: Boolean(selectedCompany?.id),
    refetchOnWindowFocus: false,
  });

  useQuery({
    queryKey: ['ai-competitive-intelligence-company', selectedCompany?.id, activeTab, filters],
    queryFn: () =>
      getCompanyAICompetitiveIntelligence(selectedCompany?.id ?? -1, {
        page: filters.page,
        page_size: filters.pageSize,
        performance: filters.performance,
        search: filters.search,
        ...(isOverall
          ? { strongest_categories: filters.strongestCategories, weakest_categories: filters.weakestCategories }
          : { category: activeTab }),
      }),
    enabled: Boolean(selectedCompany?.id),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    onSuccess: (data) => setItems(data),
  });

  const rows = items ?? [];
  const categoryStats = stats?.categories.find((category) => category.category === activeTab);
  const hasData = (stats?.categories.length ?? 0) > 0;

  const leadingCount = (isOverall ? stats?.overall.leading_count : categoryStats?.leading_count) ?? 0;
  const laggingCount = (isOverall ? stats?.overall.lagging_count : categoryStats?.lagging_count) ?? 0;
  const topStrongest = stats?.overall.top_strongest ?? null;
  const topWeakest = stats?.overall.top_weakest ?? null;
  const averageScore = (isOverall ? stats?.overall.average : categoryStats?.average) ?? null;

  return (
    <>
      {statsLoading && (
        <LoaderContainer>
          <LoaderRibbed active />
        </LoaderContainer>
      )}
      {!statsLoading && !hasData && <RecommendationsNotFound />}
      {!statsLoading && hasData && (
        <ContentContainer>
          <ChartSection>
            <AICIAvgScoreChart data={stats?.categories ?? []} />
          </ChartSection>
          <PillsSection>
            <CompanyAiCompetitiveIntelligenceCategoryPills
              stats={stats}
              activeTab={activeTab}
              onChange={handleTabChange}
            />
          </PillsSection>
          <SummarySection>
            <AICICompanySummary
              leadingCount={leadingCount}
              laggingCount={laggingCount}
              topStrongest={topStrongest}
              topWeakest={topWeakest}
              filters={tableFilters}
              onFiltersChange={handleFiltersChange}
              categoryLabel={activeCategoryLabel}
              activeTab={activeTab}
            />
          </SummarySection>
          <FiltersSection>
            <AICICompanyTableFilters
              filters={tableFilters}
              onFiltersChange={handleFiltersChange}
              onSearchChange={handleSearchChange}
              categoryMode={!isOverall}
            />
          </FiltersSection>
          <AICICompanyCommunityTable
            rows={rows}
            category={activeCategoryLabel}
            averageScore={averageScore}
            page={filters.page}
            pageSize={filters.pageSize}
            total={totalItemsCount}
            onPageChange={setPage}
          />
        </ContentContainer>
      )}
    </>
  );
};

export default CompanyAiCompetitiveIntelligence;
